import { forwardRef } from 'react'

export interface ShareTarotData {
  draw_date: string
  orientation: 'upright' | 'reversed'
  /** Optional dataURL of the card image. If set, used in place of `card.image_url` for offline capture. */
  imageDataUrl?: string | null
  card: {
    position: number
    name_th: string
    name_en: string
    image_url: string | null
    emoji: string | null
    keywords: string[]
    lucky_color: string | null
    lucky_numbers: string[]
    meaning: string
  }
}

// Hardcoded values — CSS vars don't resolve inside html-to-image canvas
const C = {
  bg:          '#16120d',
  bg2:         '#221a0f',
  bg3:         '#1c1508',
  gold:        '#d4b884',
  goldMid:     '#b8945a',
  goldDeep:    '#8a6e40',
  text:        '#f0ead8',
  textMuted:   '#b8b09a',
  textSoft:    '#7a7260',
  surface:     'rgba(255,255,255,0.06)',
  border:      'rgba(212,184,132,0.20)',
  borderLight: 'rgba(212,184,132,0.12)',
  parchment:   '#f7ecd0',
  parchment2:  '#d9c896',
}

const ROMAN: Record<number, string> = {
  0: '0', 1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V', 6: 'VI', 7: 'VII',
  8: 'VIII', 9: 'IX', 10: 'X', 11: 'XI', 12: 'XII', 13: 'XIII', 14: 'XIV',
  15: 'XV', 16: 'XVI', 17: 'XVII', 18: 'XVIII', 19: 'XIX', 20: 'XX', 21: 'XXI',
}

function formatThaiDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  const days = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัส', 'ศุกร์', 'เสาร์']
  const months = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
  return `วัน${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear() + 543}`
}

// Renders at 540×960 px — caller uses toPng(ref, { pixelRatio: 2 }) → 1080×1920
export const TarotShareCard = forwardRef<HTMLDivElement, { data: ShareTarotData }>(
  ({ data }, ref) => {
    const { card, orientation, draw_date, imageDataUrl } = data
    const isReversed = orientation === 'reversed'
    // Use preloaded dataURL if provided (avoids race with html-to-image fetch)
    const imgSrc = imageDataUrl ?? (card.image_url ? `${window.location.origin}${card.image_url}` : null)

    // Scale meaning font + line-height by length so long Gemini outputs still fit
    const meaningLen = (card.meaning ?? '').length
    const meaningFontSize =
      meaningLen <= 180 ? 13
      : meaningLen <= 260 ? 12
      : meaningLen <= 360 ? 11
      : 10.5
    const meaningLineHeight =
      meaningLen <= 260 ? 1.7
      : meaningLen <= 360 ? 1.6
      : 1.55

    return (
      <div
        ref={ref}
        style={{
          width: '540px',
          height: '960px',
          background: `linear-gradient(155deg, ${C.bg} 0%, ${C.bg2} 45%, ${C.bg3} 70%, ${C.bg} 100%)`,
          fontFamily: '"Noto Sans Thai", "Prompt", "Sarabun", system-ui, sans-serif',
          color: C.text,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Radial gold glow top */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '300px', pointerEvents: 'none',
          background: 'radial-gradient(ellipse 90% 60% at 50% 0%, rgba(212,184,132,0.14) 0%, transparent 70%)',
        }} />
        {/* Top accent line */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
          background: `linear-gradient(90deg, transparent 0%, ${C.gold} 30%, ${C.gold} 70%, transparent 100%)`,
        }} />
        {/* Bottom accent line */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px',
          background: `linear-gradient(90deg, transparent 0%, ${C.goldMid} 30%, ${C.goldMid} 70%, transparent 100%)`,
        }} />

        {/* ── HEADER ────────────────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '14px', paddingTop: '32px', paddingBottom: '16px',
          borderBottom: `1px solid ${C.border}`,
          marginLeft: '32px', marginRight: '32px',
        }}>
          <img
            src={`${window.location.origin}/logo.png`}
            width={48} height={48}
            style={{ borderRadius: '12px', flexShrink: 0 }}
          />
          <div>
            <div style={{ fontSize: '22px', fontWeight: 800, color: C.gold, letterSpacing: '0.3px', lineHeight: 1.2 }}>
              Amulet Digit
            </div>
            <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>
              🃏 ไพ่ยิปซีประจำวัน • ทำนายฝัน • เลขมงคล
            </div>
          </div>
        </div>

        {/* ── DATE BADGE ─────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '14px' }}>
          <div style={{
            padding: '6px 24px', borderRadius: '100px',
            border: `1.5px solid ${C.goldMid}`,
            background: 'rgba(184,148,90,0.12)',
          }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: C.gold }}>
              {formatThaiDate(draw_date)}
            </span>
          </div>
        </div>

        {/* ── CARD IMAGE ─────────────────────────────────────── */}
        <div style={{
          margin: '16px auto 0',
          width: '236px', height: '380px',
          borderRadius: '14px',
          overflow: 'hidden',
          background: `linear-gradient(180deg, ${C.parchment} 0%, ${C.parchment2} 100%)`,
          boxShadow: `
            0 0 0 1px #6b5226,
            0 0 0 5px ${C.parchment},
            0 0 0 7px ${C.goldMid},
            0 20px 50px rgba(0,0,0,0.45)
          `,
          position: 'relative',
        }}>
          {/* Card image */}
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {imgSrc ? (
              <img
                src={imgSrc}
                alt={card.name_th}
                crossOrigin="anonymous"
                style={{
                  width: '100%', height: '100%', objectFit: 'contain',
                  transform: isReversed ? 'rotate(180deg)' : 'none',
                  filter: 'drop-shadow(0 4px 8px rgba(107,82,38,0.25))',
                }}
              />
            ) : (
              <span style={{ fontSize: '110px', transform: isReversed ? 'rotate(180deg)' : 'none' }}>
                {card.emoji ?? '🃏'}
              </span>
            )}
          </div>

          {/* Roman numeral badge — top */}
          <div style={{ position: 'absolute', top: '8px', left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
            <div style={{
              padding: '3px 14px',
              borderRadius: '100px',
              background: 'rgba(247,236,208,0.94)',
              border: `1px solid ${C.goldMid}`,
              boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
            }}>
              <span style={{
                fontSize: '13px', fontWeight: 700, letterSpacing: '0.25em',
                color: '#6b5226', fontFamily: 'Georgia, serif',
              }}>
                {ROMAN[card.position]}
              </span>
            </div>
          </div>

          {/* Title plate bottom */}
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            paddingTop: '32px', paddingBottom: '10px', paddingLeft: '12px', paddingRight: '12px',
            textAlign: 'center',
            background: 'linear-gradient(to top, rgba(26,17,53,0.95) 35%, rgba(26,17,53,0.7) 65%, transparent 100%)',
          }}>
            <div style={{
              fontSize: '20px', fontWeight: 800, lineHeight: 1.15, marginTop: '4px',
              color: C.parchment, fontFamily: 'Georgia, "Cormorant Garamond", serif',
              textShadow: '0 2px 6px rgba(0,0,0,0.7)',
            }}>
              {card.name_th}
            </div>
            <div style={{
              fontSize: '10px', letterSpacing: '0.35em', marginTop: '4px', fontWeight: 700,
              color: C.gold, fontFamily: 'Georgia, serif',
            }}>
              {card.name_en.toUpperCase()}
            </div>
          </div>
        </div>

        {/* ── MEANING ────────────────────────────────────────── */}
        <div style={{
          margin: '14px 28px 0',
          flex: 1, minHeight: 0,
          padding: '14px 18px',
          borderRadius: '16px',
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderLeft: `3px solid ${C.gold}`,
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            fontSize: '10px', fontWeight: 700, color: C.gold,
            letterSpacing: '0.25em', marginBottom: '8px',
            flexShrink: 0,
          }}>
            ✨ ข้อความสำหรับวันนี้
          </div>
          <p style={{
            margin: 0,
            fontSize: `${meaningFontSize}px`,
            lineHeight: meaningLineHeight,
            color: C.text,
            whiteSpace: 'pre-line',
            wordBreak: 'break-word',
          }}>
            {card.meaning}
          </p>
        </div>

        {/* ── FOOTER ─────────────────────────────────────────── */}
        <div style={{
          margin: '10px 28px 20px',
          flexShrink: 0,
          padding: '12px 16px',
          borderRadius: '16px',
          background: 'rgba(212,184,132,0.08)',
          border: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <div style={{ fontSize: '10px', color: C.textMuted, marginBottom: '2px' }}>
              เปิดไพ่ฟรี รับเลขมงคลทุกวัน
            </div>
            <div style={{ fontSize: '15px', fontWeight: 800, color: C.gold }}>
              www.amuletdigit.com
            </div>
          </div>
          <div style={{
            padding: '8px 16px', borderRadius: '100px',
            background: `linear-gradient(135deg, ${C.goldMid} 0%, ${C.goldDeep} 100%)`,
            fontSize: '12px', fontWeight: 700, color: '#fff',
            boxShadow: '0 4px 14px rgba(184,148,90,0.4)',
          }}>
            เปิดไพ่ 🃏
          </div>
        </div>
      </div>
    )
  }
)
TarotShareCard.displayName = 'TarotShareCard'
